<template>
    <div class="container" v-if="!isTableLoading">
      <p class="title">Ürünün Diğer Seçenekleri</p>
      <div class="wrapper">
        <div class="buttons-row">
          <button
            class="action-button"
            :class="{ 'active': activeTab === TAB_TYPES.VARIANTS }"
            @click="activeTab = TAB_TYPES.VARIANTS"
          >
            Ürünün Diğer Varyantları
          </button>
          <button
            class="action-button"
            :class="{ 'active': activeTab === TAB_TYPES.GROUPED }"
            @click="activeTab = TAB_TYPES.GROUPED"
          >
            Aynı Model Koduyla Gruplanan Ürünler
          </button>
        </div>
        </div>
        
        <div v-if="isTableReady">
          <ProductVariantsTableWrapper
            :selectedCurrency="selectedCurrency"
            :category="categoryId"
            :tableData="activeTab === TAB_TYPES.VARIANTS ? variantsTableData : groupedTableData"
            :totalCount="pagedListTotal"
            :loading="isListPaginating"
            :variantRefs="activeTab === TAB_TYPES.VARIANTS ? 'productDetailProductVariants' : 'productDetailGroupedProducts'"
            :attributes="activeTab === TAB_TYPES.VARIANTS ? variantsAttributes : groupedAttributes"
            :hasVariantAttribute="props.hasVariantAttribute"
            :list-pagination="listPagination[activeTab]"
            @openAddVariantModal="emit('openAddVariantModal')"
            @paginationChange="onVariantsPaginationChange"
            @dataSaved="handleDataSaved"
          />
        </div>
    </div>
    <ProductSaleVariantTableOtherSkeleton v-else />
</template>
  
<script setup>
  import { ref, watch, computed } from "vue";
  import { useStore } from "vuex";
  import ProductVariantsTableWrapper from "./ProductVariantsTableWrapper.vue";
  import ProductSaleVariantTableOtherSkeleton from '@/components/Skeleton/ProductDetailSkeleton/ProductSaleVariantTableOtherSkeleton.vue';

  const props = defineProps({
    categoryId: {
      type: [Number, null],
      required: true
    },
    productId: {
      type: [Number, null],
      required: true
    },
    modelCode: {
      type: [Number, null],
      required: true
    },
    selectedCurrency: { 
      type: Function,
      required: true,
      default: () => {}
    },
    hasVariantAttribute: {
      type: Boolean,
      required: false,
      default: false
    }
  });
  const emit = defineEmits(["openAddVariantModal"]);

  const store = useStore();

  const TAB_TYPES = Object.freeze({
    VARIANTS: "productVariants",
    GROUPED: "groupedProducts"
  });

  const activeTab = ref(TAB_TYPES.VARIANTS);

  const productVariantsResponse = ref(null);
  const groupedProductsResponse = ref(null);
  const isTableLoading = ref(false)

  const variantsTableData = ref([]);
  const groupedTableData = ref([]);
  const variantsAttributes = ref([]);
  const groupedAttributes = ref([]);

  const DEFAULT_PAGINATION = { page: 0, size: 25 };
  const listPagination = ref({
    [TAB_TYPES.VARIANTS]: { ...DEFAULT_PAGINATION },
    [TAB_TYPES.GROUPED]: { ...DEFAULT_PAGINATION }
  });
  const isInitialLoad = ref(true);
  const isListPaginating = ref(false);
  const recentlySavedTab = ref(null);
  const prevActiveTab = ref(activeTab.value);

  const pagedListTotal = computed(() => {
    const res = activeTab.value === TAB_TYPES.VARIANTS
      ? productVariantsResponse.value
      : groupedProductsResponse.value;
    return Number(res?.totalElements ?? res?.totalCount ?? 0);
  });

  const fetchGroupedProducts = async () => {
    if (isInitialLoad.value) isTableLoading.value = true;
    else isListPaginating.value = true;
    try {
      const response = await store.dispatch('products/getProductsBySameModelCode', {
        productId: props.productId,
        categoryId: props.categoryId,
        modelCode: props.modelCode,
        page: listPagination.value[TAB_TYPES.GROUPED].page,
        size: listPagination.value[TAB_TYPES.GROUPED].size,
      });
      return response;
    } finally {
      if (isInitialLoad.value) {
        isTableLoading.value = false;
        isInitialLoad.value = false;
      } else {
        isListPaginating.value = false;
      }
    }
  }

  const fetchProductVariants = async () => {
    if (isInitialLoad.value) isTableLoading.value = true;
    else isListPaginating.value = true;
    try {
      const response = await store.dispatch('products/getProductVariants', {
        productId: props.productId,
        page: listPagination.value[TAB_TYPES.VARIANTS].page,
        size: listPagination.value[TAB_TYPES.VARIANTS].size,
      });
      return response;
    } finally {
      if (isInitialLoad.value) {
        isTableLoading.value = false;
        isInitialLoad.value = false;
      } else {
        isListPaginating.value = false;
      }
    }
  }

  const onVariantsPaginationChange = ({ page, size }) => {
    const p = listPagination.value[activeTab.value];
    p.page = page;
    p.size = size;
  };

  const isTableReady = computed(() => {
    const hasCurrency = !!props.selectedCurrency.currencyCode;
    
    
    return !isTableLoading.value && hasCurrency;
  });

  const handleDataSaved = (savedRows) => {
    const currentTab = activeTab.value;
    recentlySavedTab.value = currentTab;
    if (currentTab === TAB_TYPES.VARIANTS) {
      variantsTableData.value = savedRows;
    } else {
      groupedTableData.value = savedRows;
    }
  };

  watch(
    [activeTab, listPagination],
    async () => {
    const newTab = activeTab.value;
    const isTabSwitch = prevActiveTab.value !== newTab;
    prevActiveTab.value = newTab;

    if (isTabSwitch && recentlySavedTab.value === newTab) {
      recentlySavedTab.value = null;
      return;
    }

    let result;
    if (newTab === TAB_TYPES.VARIANTS) {
      productVariantsResponse.value = await fetchProductVariants();
      result = productVariantsResponse.value;
    } else {
      groupedProductsResponse.value = await fetchGroupedProducts();
      result = groupedProductsResponse.value;
    }

    const rawData = result?.content ?? [];
    const formattedData = rawData.map(item => ({
      ...item,
      duration: item.preparingTime ?? item.duration ?? null, 
      commissionRate: item.commissionRateValue ?? null,
      commissionAmount: item.commissionAmountValue ?? null,
      stock: item.quantity ?? null,
      originalStockCode: item.stockCode ?? null,
      
      id: item.productId || item.id || Math.random(),
      validations: {
        listPrice: { error: false, message: null, isDirty: false, isLoading: false },
        salePrice: { error: false, message: null, isDirty: false, isLoading: false },
        stock: { error: false, message: null, isDirty: false, isLoading: false },
        duration: { error: false, message: null, isDirty: false, isLoading: false },
        vatRate: { error: false, message: null, isDirty: false, isLoading: false },
        gtin: { error: false, message: null, isDirty: false, isLoading: false },
        stockCode: { error: false, message: null, isDirty: false, isLoading: false },
      }
      
    }));
    const attrs = result?.content?.[0]?.attributes ?? [];

    if (newTab === TAB_TYPES.VARIANTS) {
      variantsAttributes.value = attrs;
      variantsTableData.value = formattedData;
    } else {
      groupedAttributes.value = attrs;
      groupedTableData.value = formattedData;
    }
  }, { deep: true, immediate: true });
</script>
  
<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
.container {
  padding: 0 24px;

  .title {
    padding: 16px 0;
    color: $colorN80;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: -0.05px;
  }
}

.wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;

  .buttons-row {
    display: flex;
    justify-content: flex-start;
    gap: 12px;
    align-items: center;

    .action-button {
      padding: 12px 20px;
      border-radius: 6px;
      border: 0.5px solid $colorN30;
      background: $colorN00;
      color: $colorN80;
      font-family: "Open Sans";
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: -0.05px;

      &.active {
        border-color: transparent;
        outline: 2px solid $colorM50;
        outline-offset: -2px;
        background: $colorM05;
        font-weight: 700;
      }
    }
  }
}

</style>
  